import React, { Fragment, useRef } from "react";
import { useEffectOnce } from "../../hooks/useeffectonce";
import { useSelector, useDispatch } from "react-redux";
import {
  setProcessing,
  setPageValue,
  loadPage,
  saveEntity,
  disposePage,
} from "../../redux/slices/reportsmenupageslice";
import { addNotification } from "../../redux/slices/notificationsslice";
import { hideAppDialog, setAppDialog } from "../../redux/slices/appdialogslice";
import { useNavigate, useLocation } from "react-router-dom";
import { apiErrorsHandler, getErrorMessages } from "../../api/httpclient";
import {
  Toolbar,
  ToolbarItem,
  ToolbarSeparator,
  ToolbarSpacer,
  Button,
  DropDownButton,
} from "@progress/kendo-react-buttons";
import { MenuDesigner } from "../../components/menudesigner";
import { Page } from "../page";
import { isEmptyObject, iconsData } from "../../lib/utils";
import { Loader } from "@progress/kendo-react-indicators";

import "./reportsmenupage.css";

import { useTranslation } from "react-i18next";

export const ReportsMenuPage = (props) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.currentUser);
  const currentTheme = useSelector((state) => state.currentTheme);
  const appearance = useSelector((state) => state.appearance);
  const pageState = useSelector((state) => state.reportsMenuPage);
  const navigate = useNavigate();

  const { t } = useTranslation();

  /*initial page load*/
  useEffectOnce(() => {
    dispatch(loadPage(t)).then(
      (response) => {},
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("reports.loading_errormessage"),
                type: "error",
                closable: true,
                duration: 3,
                position: "topRight",
              })
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: 3,
                position: "topRight",
              })
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("reports.unknown_error"),
              type: "error",
              closable: true,
              duration: 3,
              position: "topRight",
            })
          );
          navigate("/", { replace: true });
        };

        //console.log(action);
        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );

    return () => {
      dispatch(disposePage(true));
    };
  }, []);
  /*initial load*/

  const handleOnGroupExpandToggle = (id) => {
    dispatch(
      setPageValue({
        id: "groups",
        value: pageState.groups.map((e) => {
          if (e.Id == id) return { ...e, IsExpanded: !e.IsExpanded };
          else return e;
        }),
      })
    );
  };

  const handleOnRemoveGroup = (id) => {
    dispatch(
      setAppDialog({
        isVisible: true,
        className: "reports-menu-page-are-you-sure",
        title: t("reports.warning"),
        content: t("reports.areyousure"),
        actions: {
          yesButton: {
            title: t("reports.yes"),
            handler: () => {
              dispatch(
                setPageValue({
                  id: "groups",
                  value: pageState.groups.filter((e) => e.Id != id),
                })
              );
              dispatch(hideAppDialog());
            },
          },
          noButton: { title: t("reports.no")},
        },
      })
    );
  };

  const handleOnAddGroup = () => {
    dispatch(
      setPageValue({
        id: "addGroup",
        value: {
          formKey: new Date().toString(),
          isOpen: true,
          id: null,
          title: "",
          icon: 0,
        },
      })
    );
  };

  const handleOnAddGroupValidate = (values) => {
    let formErrors = {};

    let title = values["title"];
    let icon = values["icon"];

    if (!title || title.trim().length == 0)
      formErrors = Object.assign(formErrors, {
        title: t("reports.title"),
      });

    if (!icon || icon <= 0)
      formErrors = Object.assign(formErrors, {
        icon: t("reports.icon"),
      });

    if (isEmptyObject(formErrors)) return undefined;

    return formErrors;
  };

  const handleOnAddGroupValueChange = (item) => {
    let id = item.id;
    let value = item.value;

    let obj = { ...pageState.addGroup };
    obj[id] = value;

    dispatch(setPageValue({ id: "addGroup", value: obj }));
  };

  const handleOnAddGroupEdit = (id, title, icon) => {
    dispatch(
      setPageValue({
        id: "addGroup",
        value: {
          formKey: new Date().toString(),
          isOpen: true,
          id: id,
          title: title,
          icon: iconsData.find((e) => e.Icon == icon).Id,
        },
      })
    );
  };

  const handleOnAddGroupSave = () => {
    let formErrors = handleOnAddGroupValidate(pageState.addGroup);
    if (formErrors !== undefined) return;

    let id = pageState.addGroup.id;
    let title = pageState.addGroup.title;
    let icon = iconsData.find((e) => e.Id == pageState.addGroup.icon).Icon;

    let groups = pageState.groups;

    if (id) {
      //edit
      dispatch(
        setPageValue({
          id: "groups",
          value: pageState.groups.map((e) => {
            if (e.Id == id) return { ...e, Title: title, Icon: icon };
            else return e;
          }),
        })
      );

      dispatch(
        setPageValue({
          id: "addGroup",
          value: {
            formKey: new Date().toString(),
            isOpen: false,
            id: null,
            title: "",
            icon: 0,
          },
        })
      );

      return;
    }

    //add group
    let group = {
      Id: 0,
      Title: title,
      Icon: icon,
      OrderId: groups.length + 1,
      Items: [],
    };

    if (groups.length == 0) group.Id = 1;
    else group.Id = Math.max(...groups.map((g) => g.Id)) + 1;

    dispatch(
      setPageValue({ id: "groups", value: [...pageState.groups, group] })
    );

    dispatch(
      setPageValue({
        id: "addGroup",
        value: {
          formKey: new Date().toString(),
          isOpen: false,
          id: null,
          title: "",
          icon: 0,
        },
      })
    );
  };

  const handleOnDrop = (item) => {
    let type = item.type;
    let groups = pageState.groups;
    let reports = pageState.reports;

    if (type == 1) {
      //right report on group => insert right report at the end of the group
      let reportId = item.reportId;
      let reportTitle = item.reportTitle;
      let reportReferenceKey = item.reportReferenceKey;
      let groupId = item.groupId;

      //make sure report does not belong to group
      let group = groups.find((e) => e.Id == groupId);
      if (group.Items && group.Items.find((e) => e.ReportId == reportId))
        return;

      //add report to the end of group
      let report = {
        Id:
          !group.Items || group.Items.length == 0
            ? 1
            : Math.max(...group.Items.map((e) => e.Id)) + 1,
        Title: reportTitle,
        ReferenceKey: reportReferenceKey,
        ReportId: reportId,
        OrderId:
          !group.Items || group.Items.length == 0
            ? 1
            : Math.max(...group.Items.map((e) => e.OrderId)) + 1,
      };

      dispatch(
        setPageValue({
          id: "groups",
          value: pageState.groups.map((e) => {
            if (e.Id != groupId) return e;
            else return { ...e, Items: [...e.Items, report], IsExpanded: true };
          }),
        })
      );
    } else if (type == 2) {
      //reorder group
      let sourceGroupId = item.sourceGroupId;
      let targetGroupId = item.targetGroupId;

      //source and target group must be different
      if (sourceGroupId == targetGroupId) return;

      let sourceGroup = groups.find((e) => e.Id == sourceGroupId);

      //remove source group from list
      let groupsState = groups.filter((e) => e.Id != sourceGroupId);

      //insert it before the target group
      //get target group index
      let targetGroupIndex = groupsState.findIndex(
        (e) => e.Id == targetGroupId
      );

      groupsState.splice(targetGroupIndex, 0, sourceGroup);

      groupsState = groupsState.map((e, index) => ({ ...e, OrderId: ++index }));

      dispatch(setPageValue({ id: "groups", value: groupsState }));
    } else if (type == 3) {
      //reorder left group
      let sourceReportIdAuto = item.sourceReportIdAuto;
      let sourceReportId = item.sourceReportId;
      let targetReportIdAuto = item.targetReportIdAuto;
      let targetReportId = item.targetReportId;
      let sourceGroupId = item.sourceGroupId;
      let targetGroupId = item.targetGroupId;

      //the reports must be different but the groups must be the same
      if (
        sourceReportId == targetReportId ||
        sourceReportIdAuto == targetReportIdAuto ||
        sourceGroupId != targetGroupId
      )
        return;

      let group = groups.find((e) => e.Id == sourceGroupId);
      let sourceReport = group.Items.find((e) => e.Id == sourceReportIdAuto);

      if (!sourceReport) return;

      let itemsState = [...group.Items];

      //remove source report from list
      itemsState = itemsState.filter((e) => e.Id != sourceReportIdAuto);

      //insert it before the target report
      //get target report index
      let targetReportIndex = itemsState.findIndex(
        (e) => e.Id == targetReportIdAuto
      );

      itemsState.splice(targetReportIndex, 0, sourceReport);

      itemsState = itemsState.map((e, index) => ({ ...e, OrderId: ++index }));

      dispatch(
        setPageValue({
          id: "groups",
          value: pageState.groups.map((e) => {
            if (e.Id != group.Id) return e;

            return { ...e, Items: itemsState };
          }),
        })
      );
    } else if (type == 4) {
      //insert source report into the target group before the target report
      let sourceReportId = item.sourceReportId;
      let sourceReportTitle = item.sourceReportTitle;
      let sourceReportReferenceKey = item.sourceReportReferenceKey;
      let targetReportIdAuto = item.targetReportIdAuto;
      let targetReportId = item.targetReportId;
      let targetGroupId = item.targetGroupId;

      //the reports must be different
      if (sourceReportId == targetReportId) return;

      let targetGroup = groups.find((e) => e.Id == targetGroupId);

      //the taget group must not contain the source report
      if (targetGroup.Items.find((e) => e.ReportId == sourceReportId)) return;

      let targetReport = targetGroup.Items.find(
        (e) => e.Id == targetReportIdAuto
      );

      if (!targetReport) return;

      let itemsState = [...targetGroup.Items];

      //insert it before the target report
      //get target report index
      let targetReportIndex = itemsState.findIndex(
        (e) => e.Id == targetReportIdAuto
      );

      let report = {
        Id: Math.max(...targetGroup.Items.map((e) => e.Id)) + 1,
        Title: sourceReportTitle,
        ReferenceKey: sourceReportReferenceKey,
        ReportId: sourceReportId,
        OrderId: 0,
      };

      itemsState.splice(targetReportIndex, 0, report);

      itemsState = itemsState.map((e, index) => ({ ...e, OrderId: ++index }));

      dispatch(
        setPageValue({
          id: "groups",
          value: pageState.groups.map((e) => {
            if (e.Id != targetGroupId) return e;

            return { ...e, Items: itemsState };
          }),
        })
      );
    }
  };

  const handleOnAddGroupClose = () =>
    dispatch(
      setPageValue({
        formKey: new Date().toString(),
        id: "addGroup",
        value: { isOpen: false, id: null, title: "", icon: 0 },
      })
    );

  const handleOnClear = () => {
    if (!pageState.groups || pageState.groups.length < 1) return;

    dispatch(
      setAppDialog({
        isVisible: true,
        className: "reports-menu-page-are-you-sure",
        title: t("reports.warning"),
        content: t("reports.areyousure1"),
        actions: {
          yesButton: {
            title: t("reports.yes"),
            handler: () => {
              dispatch(setPageValue({ id: "groups", value: [] }));
              dispatch(hideAppDialog());
            },
          },
          noButton: { title: t("reports.no")},
        },
      })
    );
  };

  const handleOnRemoveReport = (id, groupId) => {
    dispatch(
      setAppDialog({
        isVisible: true,

        className: "reports-menu-page-are-you-sure",
        title: t("reports.warning"),
        content:
          t("reports.areyousure2"),
        actions: {
          yesButton: {
            title: t("reports.yes"),
            handler: () => {
              dispatch(
                setPageValue({
                  id: "groups",
                  value: pageState.groups.map((e) => {
                    if (e.Id != groupId) return e;

                    return { ...e, Items: e.Items.filter((r) => r.Id != id) };
                  }),
                })
              );

              dispatch(hideAppDialog());
            },
          },
          noButton: { title: t("reports.no") },
        },
      })
    );
  };

  const handleOnSave = (e) => {
    dispatch(saveEntity(t)).then(
      (response) => {},
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("reports.loading_errormessage"),
                type: "error",
                closable: true,
                duration: 3,
                position: "topRight",
              })
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: 3,
                position: "topRight",
              })
            );
          }
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("reports.unknown_error"),
              type: "error",
              closable: true,
              duration: 3,
              position: "topRight",
            })
          );
        };

        //console.log(action);
        apiErrorsHandler({}, dispatch, null, err, "", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  };

  return (
    <Fragment>
      <Page
        id="reportsMenuPage"
        className="reports-menu-page"
        title={t("reports.menu")}
        animation={props.animation}
      >
        <Fragment>
          {pageState.pageLoaded && (
            <Fragment>
              <div className="toolbar">
                <Toolbar>
                  <ToolbarItem>
                    <Button
                      disabled={pageState.processing}
                      style={{ color: currentTheme.primary }}
                      
                      icon="plus"
                      onClick={handleOnSave}
                    >
                      {t("reports.save")}
                    </Button>
                  </ToolbarItem>
                  <ToolbarSeparator />
                  <ToolbarItem>
                    <Button
                      disabled={pageState.processing}
                      style={{ color: currentTheme.primary }}
                      
                      icon="plus"
                      onClick={handleOnClear}
                    >
                      {t("reports.clear")}
                    </Button>
                  </ToolbarItem>
                </Toolbar>
              </div>
              <div className="designer">
                <MenuDesigner
                  addGroupState={{ ...pageState.addGroup }}
                  onAddGroup={handleOnAddGroup}
                  onAddGroupValidate={handleOnAddGroupValidate}
                  onAddGroupValueChange={handleOnAddGroupValueChange}
                  onAddGroupSave={handleOnAddGroupSave}
                  onAddGroupClose={handleOnAddGroupClose}
                  onAddGroupEdit={handleOnAddGroupEdit}
                  onDrop={handleOnDrop}
                  groups={pageState.groups}
                  reports={pageState.reports}
                  onGroupExpandToggle={handleOnGroupExpandToggle}
                  onRemoveGroup={handleOnRemoveGroup}
                  onRemoveReport={handleOnRemoveReport}
                />
              </div>
            </Fragment>
          )}
          {pageState.processing && (
            <div className="page-loader">
              <Loader
                size={"large"}
                type="infinite-spinner"
                themeColor={"primary"}
              />
              {pageState.processingText && (
                <div style={{ fontSize: 13, color: currentTheme.primary }}>
                  {pageState.processingText}
                </div>
              )}
            </div>
          )}
        </Fragment>
      </Page>

      <style>
        {`
                    /*page class*/
                    .reports-menu-page
                    {
                        background-color: transparent;
                        padding: 0;
                        width: 100%;
                         direction:${appearance.isRightToLeft ? "rtl" : "ltr"}  

                    }

                    .reports-menu-page .content
                    {
                        display: flex !important;
                        flex-direction: column;
                        width: 100%;
                        height: 100%
                    }

                    .reports-menu-page .content .designer
                    { 
                        flex: 1;
                        display: flex;
                        flex-direction: column;
                        height: 100vh;
                        overflow-y: auto;
                    }

                    /*form toolbar*/
                    .reports-menu-page .content .toolbar .k-toolbar
                    {
                        width: 100%;
                        border-left: none !important;
                        border-bottom: 1px solid ${currentTheme.separatorColor};
                        border-top: none !important;
                        border-right: none !important;
                        background-color: ${currentTheme.toolbarBg};/*transparent;*/
                    }

                    .reports-menu-page .content .k-toolbar .k-button
                    {
                      /*the toolbar buttons*/
                      border: none;
                      padding: 6px 8px;
                      background:transparent;
                    }

                    .reports-menu-page .content .k-toolbar .k-button-text
                    {
                      /*color of the text in every toolbar button=black in azure theme*/
                      color: ${currentTheme.toolbarText};
                    }

                    .reports-menu-page .content .k-toolbar .k-button-icon
                    {
                      font-weight: 500;
                      color: ${currentTheme.primary};
                    }

                    /*are you sure to delete*/
                    .reports-menu-page-are-you-sure .k-window
                    {
                      /*width of the dialog*/
                      width: 300px;
                    }    
                    
                    .reports-menu-page-are-you-sure .k-window .k-dialog-titlebar.k-header.k-header
                    {
                        /*bg color of the header*/
                        background-color: ${currentTheme.primary};
                    }

                    @media (max-width: 1024px) {
                        .reports-menu-page .content {
                            min-height: 100%;
                            overflow-y: auto;
                        }

                        .reports-menu-page .content .designer {
                            height: auto;
                            min-height: 0;
                            overflow: visible;
                        }

                        .reports-menu-page .content .toolbar .k-toolbar {
                            flex-wrap: wrap;
                        }

                        .reports-menu-page-are-you-sure .k-window {
                            box-sizing: border-box;
                            max-width: calc(100vw - 32px);
                            width: min(300px, calc(100vw - 32px));
                        }
                    }
                    `}
      </style>
    </Fragment>
  );
};

//export const UsersPage = {Component: Users};
